/**
 * QaBadgeAndPanel — persistent "TEST ACCOUNT" badge that appears on
 * every screen when the current user is our QA test account, plus a
 * dev panel that opens with `?qa=1` in the URL.
 *
 * Product contract:
 *   • Badge is UNCONDITIONALLY visible for the QA user across every
 *     rider surface. It's a small pill anchored top-right that never
 *     covers primary content.
 *   • `?qa=1` toggles the dev panel. The flag itself grants no
 *     permissions — every button in the panel hits a `require_qa_user`
 *     endpoint. A hostile user setting `?qa=1` sees an empty panel
 *     because every request 403s.
 *   • The panel lets the owner walk the whole flow with test cards:
 *     create a trip, advance ride status, simulate payment failure,
 *     toggle a synthetic "offline" state for banner testing.
 */
import React, { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Zap, ChevronDown, ChevronUp, Play, PhoneOff, Ban, RefreshCw, Trash2 } from "lucide-react";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { toast } from "sonner";

// Read once at mount and re-read on route change so devs can toggle
// the panel with a URL edit without a full reload.
function useQaFlag() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get("qa") === "1";
}

export default function QaBadgeAndPanel() {
  const { user } = useAuth();
  const isQa = !!user?.is_qa_test_account;
  const qaFlag = useQaFlag();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastTrip, setLastTrip] = useState(null);
  const [offlineSim, setOfflineSim] = useState(false);

  const createTrip = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/qa/trip/create", {});
      setLastTrip(data);
      toast.success(`Test trip created (${data.id.slice(0, 12)}…)`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not create trip");
    } finally { setBusy(false); }
  }, [busy]);

  const advance = useCallback(async (target = null) => {
    if (!lastTrip || busy) return;
    setBusy(true);
    try {
      const url = target
        ? `/qa/trip/${lastTrip.id}/advance-status?target=${target}`
        : `/qa/trip/${lastTrip.id}/advance-status`;
      const { data } = await api.post(url, {});
      setLastTrip(data);
      toast.success(`Trip advanced → ${data.status}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not advance");
    } finally { setBusy(false); }
  }, [lastTrip, busy]);

  const simulatePaymentFailure = useCallback(async () => {
    if (!lastTrip || busy) return;
    setBusy(true);
    try {
      await api.post(`/qa/trip/${lastTrip.id}/simulate-payment-failure`, {});
      toast.success("Payment marked failed on last test trip");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not simulate");
    } finally { setBusy(false); }
  }, [lastTrip, busy]);

  // Synthetic offline flag — dispatches a CustomEvent the resilience
  // banner listens for. Keeps the network layer untouched while still
  // exercising the offline UI reliably.
  const toggleOffline = useCallback(() => {
    const next = !offlineSim;
    setOfflineSim(next);
    window.dispatchEvent(new CustomEvent("link:qa:offline", { detail: { offline: next } }));
    toast.info(next ? "Offline banner ON (synthetic)" : "Offline banner OFF");
  }, [offlineSim]);

  // Reset QA state — two-step: preview counts, then commit.
  // Strictly deletes docs flagged is_qa_test=True across the five
  // QA-scoped collections; no other predicate is used. Backend enforces
  // the same guardrail (`_QA_RESET_FILTER = {"is_qa_test": True}`).
  const [resetPreview, setResetPreview] = useState(null);
  const openResetPreview = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get("/qa/reset/preview");
      setResetPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  }, [busy]);
  const confirmReset = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/qa/reset?confirm=true", {});
      toast.success(`Deleted ${data?.deleted?.total ?? 0} QA record(s)`);
      setResetPreview(null);
      setLastTrip(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  }, [busy]);

  if (!isQa) return null;

  return (
    <>
      {/* PERSISTENT TEST ACCOUNT BADGE — top-right, always on top. */}
      <div
        className="fixed top-2 right-2 z-[95] pointer-events-none"
        data-testid="qa-test-account-badge"
      >
        <div className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-[#ed6500] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-3 py-1 shadow-lg">
          <Zap size={11} strokeWidth={2.4} fill="currentColor" /> Test Account
        </div>
      </div>

      {/* DEV PANEL — visible only when ?qa=1 in URL. */}
      {qaFlag && (
        <div
          className="fixed left-2 bottom-2 z-[94] w-[min(340px,calc(100vw-16px))] bg-[#0A1128] text-white rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
          data-testid="qa-dev-panel"
        >
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-4 py-2.5 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-white/[0.06]"
            data-testid="qa-panel-toggle"
          >
            <span className="inline-flex items-center gap-2"><Zap size={12} strokeWidth={2.4} /> QA Panel</span>
            {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>

          {expanded && (
            <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
              <button
                type="button"
                onClick={createTrip}
                disabled={busy}
                className="w-full h-11 rounded-xl bg-[#ed6500] text-white text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="qa-create-trip"
              >
                <Play size={12} strokeWidth={2.4} fill="currentColor" /> Create Test Trip
              </button>

              {lastTrip && (
                <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3 text-[11px] space-y-2" data-testid="qa-last-trip-card">
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Last trip</span>
                    <span className="font-mono text-white/85">{lastTrip.id.slice(0, 14)}…</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60">Status</span>
                    <span
                      className="px-2 py-0.5 rounded-full bg-[#ed6500]/20 text-[#ed6500] font-semibold"
                      data-testid="qa-last-trip-status"
                    >
                      {lastTrip.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => advance()}
                      disabled={busy || lastTrip.status === "completed"}
                      className="h-8 rounded-lg bg-white/[0.08] hover:bg-white/[0.12] text-[10px] tracking-[0.18em] uppercase font-semibold disabled:opacity-40"
                      data-testid="qa-advance-status"
                    >
                      Advance ▸
                    </button>
                    <button
                      type="button"
                      onClick={() => advance("completed")}
                      disabled={busy}
                      className="h-8 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px] tracking-[0.18em] uppercase font-semibold disabled:opacity-40"
                      data-testid="qa-jump-completed"
                    >
                      Complete
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={simulatePaymentFailure}
                  disabled={busy || !lastTrip}
                  className="h-9 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-[10px] tracking-[0.18em] uppercase font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                  data-testid="qa-simulate-payment-fail"
                >
                  <Ban size={11} /> Pmt Fail
                </button>
                <button
                  type="button"
                  onClick={toggleOffline}
                  className={`h-9 rounded-lg text-[10px] tracking-[0.18em] uppercase font-semibold inline-flex items-center justify-center gap-1.5 ${
                    offlineSim ? "bg-red-500/25 text-red-300" : "bg-white/[0.06] hover:bg-white/[0.10]"
                  }`}
                  data-testid="qa-toggle-offline"
                >
                  <PhoneOff size={11} /> {offlineSim ? "Online" : "Offline"}
                </button>
              </div>

              <p className="text-[10px] text-white/50 leading-relaxed pt-1">
                All actions require the QA account server-side. `?qa=1` alone grants no permissions.
              </p>

              {/* Reset QA state — two-step preview + commit. */}
              <div className="pt-2 border-t border-white/10 space-y-2">
                {!resetPreview ? (
                  <button
                    type="button"
                    onClick={openResetPreview}
                    disabled={busy}
                    className="w-full h-9 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-[10px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
                    data-testid="qa-reset-preview"
                  >
                    <Trash2 size={11} /> Reset QA State
                  </button>
                ) : (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/25 p-3 space-y-2" data-testid="qa-reset-preview-card">
                    <div className="text-[11px] text-red-200 leading-relaxed" data-testid="qa-reset-preview-message">
                      {resetPreview.message}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[10px] text-white/70">
                      {resetPreview.collections?.map((name) => (
                        <div key={name} className="flex items-center justify-between bg-white/[0.05] rounded px-2 py-1">
                          <span className="uppercase tracking-wider text-white/50">{name}</span>
                          <span className="font-mono text-white/90" data-testid={`qa-reset-count-${name}`}>{resetPreview.counts?.[name] ?? 0}</span>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setResetPreview(null)}
                        disabled={busy}
                        className="h-8 rounded-lg bg-white/[0.08] hover:bg-white/[0.14] text-[10px] tracking-[0.18em] uppercase font-semibold disabled:opacity-40"
                        data-testid="qa-reset-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={confirmReset}
                        disabled={busy || (resetPreview.counts?.total ?? 0) === 0}
                        className="h-8 rounded-lg bg-red-500/70 hover:bg-red-500/90 text-white text-[10px] tracking-[0.18em] uppercase font-semibold disabled:opacity-40"
                        data-testid="qa-reset-confirm"
                      >
                        Delete {resetPreview.counts?.total ?? 0}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
